/**
 * Query Client — reactive cache engine for server data.
 *
 * Features:
 *   - $state-based reactive cache (Svelte 5 runes mode)
 *   - stale-while-revalidate: show cached data, refresh in background
 *   - request deduplication: same key in-flight = shared promise
 *   - manual refetch / invalidation
 *
 * Usage (in a .svelte file):
 *   const query = createQuery({
 *     key: ['courses'],
 *     fetcher: () => list_courses().then(r => r.response_data),
 *     staleTime: 5 * 60 * 1000,
 *   })
 *
 * Template:
 *   {#if query.loading}Loading...
 *   {:else if query.error}Error: {query.error}
 *   {:else}...{query.data}...{/if}
 *
 * The returned query handle is a $state proxy — template access is reactive.
 */

/** @type {Map<string, import('./types.js').QueryHandle>} */
const cache = new Map()

/** @type {Map<string, Promise<any>>} */
const inflight = new Map()

import { loadingManager } from '$lib/loading/loading-manager.js'
import { LoadingType } from '$lib/loading/types.js'

/**
 * Create or retrieve a cached query.
 * Returns the same reactive handle for the same key — components share state.
 *
 * @param {import('./types.js').QueryOptions} options
 * @returns {import('./types.js').QueryHandle}
 */
export function createQuery({ key, fetcher, staleTime = 300000, gcTime = 1800000 }) {
  const cacheKey = JSON.stringify(key)
  if (cache.has(cacheKey)) {
    const existing = cache.get(cacheKey)
    // Retry immediately if previous attempt failed with no data
    if (existing.error && existing.data === null && !existing.refreshing && !inflight.has(cacheKey)) {
      fetchQuery(cacheKey, existing, fetcher, false)
    } else if (Date.now() - existing.lastFetched > staleTime && !existing.refreshing) {
      // Background refresh if stale
      fetchQuery(cacheKey, existing, fetcher, true)
    }
    return existing
  }

  // $state in .svelte.js creates a deep reactive proxy.
  // Components reading these properties in templates re-render on change.
  const query = $state({
    data: null,
    error: null,
    errorStatus: null,
    errorCode: null,
    errorData: null,
    loading: true,
    refreshing: false,
    lastFetched: 0,
    _fetcher: fetcher,
    refetch: () => fetchQuery(cacheKey, query, fetcher, true),
  })

  cache.set(cacheKey, query)
  fetchQuery(cacheKey, query, fetcher, false)
  scheduleGC(cacheKey, gcTime)

  return query
}

const MAX_RETRIES = 1
const RETRY_DELAY_MS = 500

/**
 * Run fetcher with 1 retry on transient errors (network / 5xx).
 * @param {() => Promise<any>} fetcher
 */
async function fetchWithRetry(fetcher) {
  let lastErr
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      return await fetcher()
    } catch (err) {
      lastErr = err
      const msg = err?.message || String(err)
      const transient = msg.includes('Failed to fetch')
        || msg.includes('NetworkError')
        || /API Error: 5\d\d/.test(msg)
        || /API Error: 408/.test(msg)
        || /API Error: 429/.test(msg)
      if (!transient || attempt === MAX_RETRIES) throw err
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * (attempt + 1)))
    }
  }
  throw lastErr
}

/**
 * Fetch and update query state. Deduplicates in-flight requests.
 * @param {string} cacheKey
 * @param {import('./types.js').QueryHandle} query
 * @param {() => Promise<any>} fetcher
 * @param {boolean} isRefresh
 */
async function fetchQuery(cacheKey, query, fetcher, isRefresh) {
  // Deduplicate: reuse in-flight promise, then surface any error to this caller too
  if (inflight.has(cacheKey)) {
    await inflight.get(cacheKey)
    // The in-flight request writes to whichever handle started it, which is not
    // necessarily this one — a cache entry can be evicted or replaced mid-flight,
    // and the next createQuery() then builds a fresh handle on the same key.
    // Settle this handle explicitly: without it `loading` stays true forever and
    // the component renders a skeleton that never resolves, with no error shown.
    const settled = cache.get(cacheKey)
    if (settled && settled !== query) {
      query.data = settled.data
      query.error = settled.error
      query.errorStatus = settled.errorStatus
      query.errorCode = settled.errorCode
      query.errorData = settled.errorData
      query.lastFetched = settled.lastFetched
    }
    query.loading = false
    query.refreshing = false
    if (query.error && query.data === null) throw new Error(query.error)
    return
  }

  const isFirstLoad = query.lastFetched === 0 && !isRefresh

  if (isRefresh) {
    query.refreshing = true
  } else if (query.lastFetched > 0) {
    query.refreshing = true // stale-while-revalidate
  } else {
    query.loading = true
  }

  const loadingId = isFirstLoad
    ? loadingManager.start(LoadingType.Query, { source: cacheKey, message: 'Loading data...' })
    : null

  const promise = fetchWithRetry(fetcher)
    .then((result) => {
      query.data = result
      query.error = null
      query.errorStatus = null
      query.errorCode = null
      query.errorData = null
      query.lastFetched = Date.now()
      if (loadingId) loadingManager.finish(loadingId)
    })
    .catch((err) => {
      // Record error regardless of whether stale data exists — UI decides how to render
      query.error = err?.message || String(err)
      // Pages can distinguish typed HTTP failures (paywall, state conflict, etc.).
      query.errorStatus = err?.status ?? null
      query.errorCode = err?.code ?? null
      query.errorData = err?.data ?? null
      if (loadingId) loadingManager.finish(loadingId, { reason: 'aborted' })
    })
    .finally(() => {
      query.loading = false
      query.refreshing = false
      inflight.delete(cacheKey)
    })

  inflight.set(cacheKey, promise)
  return promise
}

/**
 * Schedule garbage collection after gcTime of inactivity.
 * @param {string} cacheKey
 * @param {number} gcTime
 */
function scheduleGC(cacheKey, gcTime) {
  setTimeout(() => {
    const entry = cache.get(cacheKey)
    if (entry && Date.now() - entry.lastFetched > gcTime) {
      cache.delete(cacheKey)
      inflight.delete(cacheKey)
    }
  }, gcTime + 1000)
}

/**
 * Manually invalidate a query — next access refetches.
 * @param {Array} key
 * @param {{ refetch?: boolean }} options
 */
export function invalidateQuery(key, { refetch = false } = {}) {
  const cache_key = JSON.stringify(key)
  const entry = cache.get(cache_key)
  if (!entry) return
  entry.lastFetched = 0
  if (!refetch) return
  const pending = inflight.get(cache_key)
  return pending ? pending.then(() => entry.refetch()) : entry.refetch()
}

/**
 * Invalidate every query whose key starts with `prefix`.
 *
 * For changes that affect a whole family of keys at once — subscribing unlocks
 * content, so every `['playlist', id]` and `['playlist-items', id]` entry goes
 * stale, not just the one the user was looking at.
 * @param {Array} prefix
 */
export function invalidateQueryPrefix(prefix) {
  for (const [cacheKey, entry] of cache) {
    let key
    try {
      key = JSON.parse(cacheKey)
    } catch {
      continue
    }
    if (!Array.isArray(key) || key.length < prefix.length) continue
    if (prefix.every((part, i) => key[i] === part)) entry.lastFetched = 0
  }
}

/**
 * Force refresh a query.
 * @param {Array} key
 */
export function refreshQuery(key) {
  const cacheKey = JSON.stringify(key)
  const entry = cache.get(cacheKey)
  if (entry && entry._fetcher) {
    fetchQuery(cacheKey, entry, entry._fetcher, true)
  }
}

/**
 * Read a query handle without creating one.
 * @param {Array} key
 * @returns {import('./types.js').QueryHandle | undefined}
 */
export function getQuery(key) {
  return cache.get(JSON.stringify(key))
}

/**
 * Invalidate all queries (on logout).
 */
export function invalidateAll() {
  cache.forEach((e) => { e.lastFetched = 0 })
}

/**
 * Clear all cached data.
 */
export function clearAll() {
  cache.clear()
  inflight.clear()
}
